/**
 * Pricing Store (S9.1 → S4.1 → S1.x/S2.x/S3.1/P2/P3 → B2.5b') — 定價設定
 * dataset + PRICE_OVERRIDE audit rows.
 *
 * TWO LINES OF WORK MEET HERE, and both hold:
 *
 * 1. THE PRICING ENGINE (develop) — price bands and their CRUD (P2/P3),
 *    pricing groups (S2.1), quantity schedules, the currency matrix, central
 *    batch special prices (S3.1), cost-record create/edit (S2.4/S1.2), the
 *    company defaults (Pricing Anchor D17, costing method, and the MINIMUM
 *    MARGIN that decides the `low_margin` price-review hold), and the
 *    dev/mock-mode `persist` slice that makes "edit → refresh → still there"
 *    true for the demo.
 *
 * 2. THE MASTERS/GraphQL SEAM (B2.5a'/B2.5b') — the guard vocabulary and the
 *    pure reducers MOVED to `@senditship/domain/pricing-admin`, where the MSW
 *    write handlers and the GraphQL pricing module import the SAME code, and
 *    the saves that have an endpoint now WRITE THROUGH instead of ending in
 *    the store.
 *
 * WRITE-THROUGH (the saves `pricingApi` has a route for):
 *
 *   1. GUARD — the shared `@senditship/domain/pricing-admin` command runs
 *      against the current dataset. A refusal throws SYNCHRONOUSLY (the same
 *      error the wire would return), so the ReasonRequiredModal and every
 *      existing `catch (error) { error.code }` branch behave exactly as
 *      before, and NOTHING is written or sent.
 *   2. OPTIMISTIC APPLY — the pure result of that same command lands in the
 *      store so the surface repaints immediately.
 *   3. ROUND-TRIP — the API call goes out; on success the store adopts the
 *      SERVER's audit row (not the optimistic copy), so any normalisation the
 *      server applied is what the operator ends up looking at.
 *   4. ROLLBACK — on failure the pre-save dataset is restored and
 *      `writeError` is set. A failed save can therefore never leave a price on
 *      screen that no backend agrees with.
 *
 * STORE-FIRST, still (no endpoint exists yet): the band CRUD, the batch
 * special prices, the cost-record create/edit, the pricing-group edit and the
 * three company defaults. They keep the S4.1 shape — reason gate → pure
 * reducer computed BEFORE `set` (a refused save writes NOTHING) → ONE atomic
 * set of dataset + PRICE_OVERRIDE audit row — and the demo `persist` slice
 * carries them across a reload. Wiring them to routes is the follow-up; it is
 * recorded here rather than faked with an endpoint that does not exist.
 *
 * Guards, carried verbatim:
 * - Every price-affecting save REQUIRES a reason (the ReasonRequiredModal is
 *   the only entry) — never silently defaulted, and the ACCEPTED reason is
 *   what lands on the PRICE_OVERRIDE row (the 「報表送出給高權限看過」review
 *   lens; S10.1 lifts it into governance).
 * - Purchase Cost is READ-ONLY after create: `CostRecordPatch` has no
 *   `purchaseCost` member, so the override is unrepresentable at type level.
 * - Fixed-price immutability ('FIXED_PRICE_LOCKED', Discribe-21) and the
 *   quantity either-or ('SCHEDULE_EITHER_OR', DD!116) reject before any write.
 * - A band write never repairs a cover: the band-set invariants REFUSE with a
 *   named code and a boundary payload (`PricingGuardError.detail`).
 */

import { create } from 'zustand';
import { createJSONStorage, devtools, persist } from 'zustand/middleware';
import type { StateStorage } from 'zustand/middleware';
import dayjs from 'dayjs';
import {
  applyBandCutMoves,
  bandSetScopeOf,
  effectiveAnchorOf,
  planBandCutMoves,
  selectCostRecord,
  validateBandSetAfterChange,
} from '@/lib/pricing';
import type { BandCutMove, BandSetViolation, BandSetViolationCode } from '@/lib/pricing';
import { pricingApi } from '@/services/api/pricing.api';
import { SEED_VERSION } from '@/stores/pricingSeedVersion';
import type {
  CostRecord,
  CurrencyPriceRow,
  CustomerGroupDiscount,
  PriceGroupBand,
  PriceGroupCode,
  PriceLevel,
  CostingMethodDefault,
  PricingAnchor,
  PricingDataset,
  PricingGroup,
  ProductPricingFields,
  QuantityPricingSchedule,
  SpecialItemPrice,
} from '@/types/api/pricing';

/**
 * B2.5a' EXTRACTION SHIM — the pure reducers below MOVED to
 * `@senditship/domain/pricing-admin` (packages/domain/src/pricing-admin/**),
 * where the MSW write handlers and the GraphQL pricing module import the SAME
 * code. These re-exports keep every existing importer (GroupMatrixTab,
 * usePricing, the pinned store/component suites) resolving from this path —
 * one implementation, no fork.
 *
 * The reducers this file still OWNS (band CRUD, batch special prices,
 * cost-record create, pricing-group patch) are the ones the domain package has
 * no body for yet; they stay here until the write surface grows them.
 */
export {
  applyCostRecordPatch,
  applyCurrencyPriceRowSave,
  applyCustomerGroupDiscountSave,
  applyCustomerPricingSave,
  applyMarkupRuleEdit,
  applyPriceGroupBandSave,
  applyPriceLevelAdd,
  applyPriceLevelSave,
  applyPricingGroupAdd,
  applyPricingGroupSave,
  applyProductPricingSave,
  applyQuantityScheduleSave,
  applySpecialPriceSave,
  type CostRecordPatch,
  type MarkupRuleEdit,
} from '@senditship/domain/pricing-admin';

import {
  PricingAdminError,
  applyCostRecordPatch,
  applyCurrencyPriceRowSave,
  applyCustomerGroupDiscountSave,
  applyMarkupRuleEdit,
  applyPriceLevelAdd,
  applyPricingGroupAdd,
  applyProductPricingSave,
  applyQuantityScheduleSave,
  applySpecialPriceSave,
  guardCurrencyPriceRowSave,
  guardProductPricingSave,
  isPricingAdminError,
  type CostRecordPatch,
  type MarkupRuleEdit,
  type PriceOverrideAuditRow as PricingAdminAuditRow,
  type PriceOverrideEntityType as PricingAdminEntityType,
  type PricingAdminErrorCode,
} from '@senditship/domain/pricing-admin';

// ---------------------------------------------------------------------------
// Audit rows — PRICE_OVERRIDE review lens (GovernanceAuditLog-shaped so S10.1
// can lift them into the global audit lens without remapping)
// ---------------------------------------------------------------------------

/**
 * What kind of pricing entity the audited save touched.
 *
 * The BASE union (including S4.1's 'so_line' and B2.5a's 'markup_rule' /
 * 'customer_pricing') lives with the row shape in
 * `@senditship/domain/pricing-admin`, so the MSW handler, this store and the
 * GraphQL module all render the same old→new one-liner. WIDENED here for the
 * entities the domain write surface has no route for yet:
 * - 'pricing_group' — the S2.1 group edit/deactivate;
 * - 'special_price_batch' — the S3.1 batch HEADER discriminator;
 * - 'price_group_band' — P2/P3, the PRICE BAND row (`PriceGroupBand`), i.e. a
 *   cut in a band set. NOT 'pricing_group', which is the customer-discount
 *   `PricingGroup` (Discribe-20): three different objects in this engine have
 *   carried the word "group", and collapsing two of them into one audit
 *   discriminator is how a review lens starts lying about what changed.
 *
 * A row minted by the server (the narrower domain union) is assignable to this
 * one, so the round-trip below can adopt it unchanged.
 */
export type PriceOverrideEntityType =
  | PricingAdminEntityType
  | 'pricing_group'
  | 'special_price_batch'
  | 'price_group_band';

/**
 * The audit row as this store holds it: the domain shape with the widened
 * `entityType`. Re-exported from this path because `AuditSection.tsx`,
 * `OverridesTab.tsx` and `soPriceOverrideAudit.ts` import it from here.
 */
export interface PriceOverrideAuditRow extends Omit<PricingAdminAuditRow, 'entityType'> {
  entityType: PriceOverrideEntityType;
}

/**
 * The store's guard vocabulary: the shared write-surface codes
 * (`PricingAdminErrorCode` — REASON_REQUIRED, RULE_NOT_FOUND,
 * RECORD_NOT_FOUND, FIXED_PRICE_LOCKED, SCHEDULE_EITHER_OR and the B2.5a'
 * additions) PLUS the codes whose rules still live in this file:
 *
 * S2.4 (cost-record CREATE):
 * - 'DUPLICATE_RECORD' — the id already names an existing cost record.
 * - 'PRODUCT_NOT_FOUND' — the productId is not in the caller-supplied
 *   product universe (the picker's option list).
 * - 'NEGATIVE_COST_INPUT' — purchaseCost / landedAllocation below zero.
 * S3.1 (central batch special prices):
 * - 'BATCH_OVERLAP' — a combo in the batch names a pair that already carries
 *   ANY active special, window ignored (RULING-5, 2026-08-29): the whole
 *   batch is rejected, never a partial write / last-write-wins clobber.
 *   Deactivate the standing row first (its own audited save).
 * - 'BATCH_CAP_EXCEEDED' — products × customers over the hard cap
 *   (SPECIAL_PRICE_BATCH_CAP).
 * - 'BATCH_EMPTY' — zero combos; a header audit row over nothing would be a
 *   lying artifact.
 * - 'BATCH_UNRESOLVABLE' (RULING-6, 2026-08-29) — a markup_pct batch names a
 *   product with no resolvable cost record at the batch's effective date; the
 *   row could never resolve and would activate unreviewed the moment a cost
 *   record later appears. Freeze (fixed) rows need no base and stay legal.
 * P2 band CRUD:
 * - `BandSetViolationCode` — the ways a band set stops being a legal cover.
 *   Bodies live in `packages/domain/src/pricing/bandSetInvariants.ts` (pure +
 *   node-testable; the node test env cannot reach a rule inside a `.tsx`, so
 *   the rule may not live in the editor). Every one is a REFUSAL that writes
 *   nothing — never a silent repair, because absorbing a removed band's range
 *   into its neighbour would re-cut a bound the operator did not choose.
 */
export type PricingGuardCode =
  | PricingAdminErrorCode
  | 'DUPLICATE_RECORD'
  | 'PRODUCT_NOT_FOUND'
  | 'NEGATIVE_COST_INPUT'
  | 'BATCH_OVERLAP'
  | 'BATCH_CAP_EXCEEDED'
  | 'BATCH_EMPTY'
  | 'BATCH_UNRESOLVABLE'
  | BandSetViolationCode;

/**
 * Machine-readable guard failure (mirrors the MSW handler error shape).
 *
 * `detail` is the optional structured payload a caller needs to say WHICH
 * boundary conflicts. Today only the band-set guards populate it; the UI
 * renders the two neighbours and the two numbers from it, so an operator
 * reads "Group B's upper bound 60 and Group C's lower bound 70 leave a gap"
 * instead of a bare code. Errors thrown by every other guard carry no detail
 * and behave exactly as before.
 *
 * ONE CLASS, TWO ORIGINS. Saves that reach the shared write surface refuse
 * with the domain's `PricingAdminError` (it carries `field`, which the MSW and
 * GraphQL legs forward); saves whose rule still lives here throw this class
 * (it carries `detail`). `Symbol.hasInstance` accepts BOTH, so the single
 * `catch (error) { if (error instanceof PricingGuardError) … }` branch every
 * surface and every pinned suite already writes keeps holding whichever leg
 * refused — that identity is what the B2.5a' shim bought by aliasing the two
 * names, kept here without giving up the band payload.
 */
export class PricingGuardError extends Error {
  readonly code: PricingGuardCode;

  readonly detail?: BandSetViolation;

  constructor(code: PricingGuardCode, detail?: BandSetViolation) {
    super(code);
    this.name = 'PricingGuardError';
    this.code = code;
    this.detail = detail;
  }

  static [Symbol.hasInstance](value: unknown): boolean {
    return (
      isPricingAdminError(value) ||
      (value instanceof Error &&
        (value as PricingGuardError).name === 'PricingGuardError' &&
        typeof (value as PricingGuardError).code === 'string')
    );
  }
}

// ---------------------------------------------------------------------------
// Pure reducers still OWNED here — the collections the shared
// `@senditship/domain/pricing-admin` write surface has no command for yet
// (price bands, batch special prices, cost-record create, pricing-group
// patch). Exported for vitest; guards throw PricingGuardError.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// P2 (2026-08-31) — PRICE BAND CRUD, the write path that did not exist.
//
// Bands choose the markup rule a level price is computed from, so every band
// write is PRICE-AFFECTING: all three actions are reason-gated
// (`REASON_REQUIRED`) and every accepted save appends a PRICE_OVERRIDE audit
// row with an old → new one-liner, exactly like the other price-affecting
// saves in this file. There is no hard delete — a retired cut is deactivated
// and stays listed (the house inactive treatment, mirroring
// `setSpecialPriceActive` / `setPricingGroupActive`).
//
// FENCES CARRIED HERE (both binding, both from the 2026-08-31 rulings):
// - R-BAND: nothing in this section changes WHAT is banded on. The bucketing
//   quantity is still `breakdown.pricingBase` for every product, whatever its
//   anchor; these reducers move the CUTS, never the operand.
// - R-AD: 群組 A–D stays the coarse layer. No seeded band's values are
//   retired or re-cut by this slice — the seeds are untouched (hence no
//   SEED_VERSION bump), and an operator's own edits are their decision, made
//   through a reason-gated, audited action rather than a migration.
//
// The INVARIANT is deliberately not written here: it lives in the pure,
// dependency-free `bandSetInvariants` module so the editor component and
// these reducers share ONE body, and so a node-environment guard can reach it
// at all (erp-web-gate-reality §2 — a rule inside a `.tsx` is unguardable in
// this repo, and re-declaring it in a test asserts the wrong function).
// ---------------------------------------------------------------------------

/**
 * Band CREATE inputs. `productGroupId` (the owning product group; absent ⇒ the
 * DEFAULT set) and `currency` are chosen HERE, at create, and only here —
 * `PriceGroupBandPatch` has neither member, so moving a band between sets or
 * between currencies is unrepresentable rather than merely discouraged (the
 * `CostRecordPatch.purchaseCost` precedent). A cut belongs to the cover it was
 * cut in; re-homing one would silently open a gap in the set it left AND in
 * the set it joined, two partitions away from the operator's attention.
 *
 * `isActive` is absent for the same reason it is absent on `PricingGroup`
 * creation: activation is its own audited action, never a side effect.
 */
export interface PriceGroupBandCreate {
  id: string;
  group: PriceGroupCode;
  minInclusive: number;
  /** `null` = open-ended top band. At most one per (set × currency). */
  maxExclusive: number | null;
  currency: string;
  /** ABSENT ⇒ the DEFAULT (unscoped) band set. Immutable after create. */
  productGroupId?: string;
}

/**
 * Editable band fields. `currency`, `productGroupId` and `isActive` are
 * deliberately ABSENT (see `PriceGroupBandCreate`). The `group` LETTER is
 * editable: it is a label on the row, markup rules key off `band.id`, and a
 * mistyped letter would otherwise be uncorrectable without deleting a cut.
 */
export interface PriceGroupBandPatch {
  group?: PriceGroupCode;
  minInclusive?: number;
  maxExclusive?: number | null;
}

function mergePriceGroupBandPatch(
  band: PriceGroupBand,
  patch: PriceGroupBandPatch
): PriceGroupBand {
  const next: PriceGroupBand = { ...band };
  if (patch.group !== undefined) next.group = patch.group;
  if (patch.minInclusive !== undefined) next.minInclusive = patch.minInclusive;
  // `maxExclusive` is nullable-not-optional: `null` IS the open-ended top
  // band, so an explicit null is stored, never deleted.
  if (patch.maxExclusive !== undefined) next.maxExclusive = patch.maxExclusive;
  return next;
}

/**
 * REFUSE-BEFORE-WRITE: adjudicate the partition a change lands in, using the
 * collection AS IT WOULD BE after the write. Throws the named violation with
 * its boundary payload attached, so the surface can say which two cuts
 * conflict. Never repairs.
 */
function assertBandSetValid(
  nextBands: readonly PriceGroupBand[],
  scope: string,
  currency: string
): void {
  const violation = validateBandSetAfterChange(nextBands, scope, currency);
  if (violation) throw new PricingGuardError(violation.code, violation);
}

/**
 * Append a band. Guards (thrown BEFORE anything is written): DUPLICATE_RECORD
 * on a taken id, then the band-set invariants over the resulting cover.
 */
export function applyPriceGroupBandAdd(
  dataset: PricingDataset,
  create: PriceGroupBandCreate
): PricingDataset {
  if (dataset.priceGroupBands.some((candidate) => candidate.id === create.id)) {
    throw new PricingGuardError('DUPLICATE_RECORD');
  }
  const band: PriceGroupBand = {
    id: create.id,
    group: create.group,
    minInclusive: create.minInclusive,
    maxExclusive: create.maxExclusive,
    currency: create.currency,
    // The scope is a row a band CARRIES, not a column every band answers —
    // an explicit `undefined` key would change the default set's serialized
    // shape (the `productGroupId` docblock's own reasoning).
    ...(create.productGroupId ? { productGroupId: create.productGroupId } : {}),
    isActive: true,
  };
  const nextBands = [...dataset.priceGroupBands, band];
  assertBandSetValid(nextBands, bandSetScopeOf(band), band.currency);
  return { ...dataset, priceGroupBands: nextBands };
}

/**
 * Patch one band's cuts in place. Guards: RECORD_NOT_FOUND, then the
 * invariants over the partition the band already belongs to (which the patch
 * cannot change, by construction).
 */
export function applyPriceGroupBandUpdate(
  dataset: PricingDataset,
  bandId: string,
  patch: PriceGroupBandPatch
): PricingDataset {
  const existing = dataset.priceGroupBands.find((candidate) => candidate.id === bandId);
  if (!existing) throw new PricingGuardError('RECORD_NOT_FOUND');
  const nextBands = dataset.priceGroupBands.map((candidate) =>
    candidate.id === bandId ? mergePriceGroupBandPatch(candidate, patch) : candidate
  );
  assertBandSetValid(nextBands, bandSetScopeOf(existing), existing.currency);
  return { ...dataset, priceGroupBands: nextBands };
}

/**
 * Flip one band's isActive. No hard delete exists for bands — a deactivated
 * cut stays listed with the house inactive treatment, and the resolver stops
 * matching it (`resolvePriceGroup`'s `isBandActive` predicate), so this is a
 * real price change rather than a cosmetic flag.
 *
 * Guards: RECORD_NOT_FOUND, then the invariants over the RESULTING active
 * cover — so deactivating an INTERIOR cut is REFUSED with `BAND_GAP` naming
 * the hole it would leave. Widen a neighbour first (its own audited save);
 * this action will not silently absorb the range.
 */
export function applyPriceGroupBandActiveSet(
  dataset: PricingDataset,
  bandId: string,
  isActive: boolean
): PricingDataset {
  const existing = dataset.priceGroupBands.find((candidate) => candidate.id === bandId);
  if (!existing) throw new PricingGuardError('RECORD_NOT_FOUND');
  const nextBands = dataset.priceGroupBands.map((candidate) =>
    candidate.id === bandId ? { ...candidate, isActive } : candidate
  );
  assertBandSetValid(nextBands, bandSetScopeOf(existing), existing.currency);
  return { ...dataset, priceGroupBands: nextBands };
}

/**
 * P3 (2026-08-31) — MOVE AN INTERIOR CUT, atomically.
 *
 * THE BREAK IT CLOSES, measured: a gapless cover admits NO legal single-band
 * edit that moves a shared boundary. Shrinking the lower band first leaves a
 * GAP; widening the upper band first leaves an OVERLAP; `validateBandSet`
 * refuses both, in both orders. So through `updatePriceGroupBand` alone an
 * interior cut could never move at all, and a product could never be re-banded
 * from Price Setup — which is the round trip the product page is supposed to
 * reflect.
 *
 * NOT THE SILENT REPAIR P2 REFUSES. A repair invents a bound nobody chose.
 * This writes the ONE bound that is already implied by "the cuts are gapless":
 * a shared boundary is one number written on two rows. The plan is computed by
 * the SAME pure `planBandCutMoves` the editor previews it with, so the operator
 * has read the neighbour's name and both numbers before committing; the
 * invariant still adjudicates the RESULT, so a move past a neighbour's far
 * bound is still refused; and the audit trail carries a row per band that
 * moved, never one row standing in for two.
 *
 * The plan is recomputed HERE rather than accepted as an argument, so a caller
 * cannot inject a move against a band in another set or currency (D27) — the
 * only bands that can move are the touching neighbours in this partition.
 */
export function applyPriceGroupBandCutMove(
  dataset: PricingDataset,
  bandId: string,
  patch: PriceGroupBandPatch
): { dataset: PricingDataset; moves: BandCutMove[] } {
  const existing = dataset.priceGroupBands.find((candidate) => candidate.id === bandId);
  if (!existing) throw new PricingGuardError('RECORD_NOT_FOUND');
  const moves = planBandCutMoves(dataset.priceGroupBands, bandId, patch);
  const patched = dataset.priceGroupBands.map((candidate) =>
    candidate.id === bandId ? mergePriceGroupBandPatch(candidate, patch) : candidate
  );
  const nextBands = applyBandCutMoves(patched, moves);
  assertBandSetValid(nextBands, bandSetScopeOf(existing), existing.currency);
  return { dataset: { ...dataset, priceGroupBands: nextBands }, moves };
}

// ---------------------------------------------------------------------------
// S3.1 — central batch special prices (DA-2 explicit-selection MVP)
// ---------------------------------------------------------------------------

/** Hard combo cap — products × customers may never exceed this (DA-2). */
export const SPECIAL_PRICE_BATCH_CAP = 50;

/**
 * ONE adjustment applied to every combo. `fixed` is a FREEZE by design
 * (DA-2 amended): the written price detaches from cost/ladder changes — the
 * UI says 凍結價格 / Freeze price, never "round". `markup_pct` keeps
 * following the resolved base at read time (% OF the Pricing Base).
 */
export type SpecialPriceBatchAdjustment =
  | { kind: 'markup_pct'; markupPct: number }
  | { kind: 'fixed'; fixedPrice: number; currency: string };

/**
 * The batch write request — EXPLICIT product/customer selections only, never
 * a range selector (R-1: nothing qty-conditioned lives anywhere on this
 * form), one adjustment, one validity window per the existing
 * SpecialItemPrice fields.
 */
export interface SpecialPriceBatchInput {
  productIds: string[];
  customerIds: number[];
  adjustment: SpecialPriceBatchAdjustment;
  validFrom: string;
  validTo: string | null;
}

export interface SpecialPriceBatchCombo {
  productId: string;
  customerId: number;
}

/**
 * Deterministic combo expansion (product-major) — the SAME order the preview
 * table renders and the reducer writes, so "preview = exactly the rows to be
 * written" holds by construction.
 */
export function expandSpecialPriceBatchCombos(
  productIds: readonly string[],
  customerIds: readonly number[]
): SpecialPriceBatchCombo[] {
  return productIds.flatMap((productId) =>
    customerIds.map((customerId) => ({ productId, customerId }))
  );
}

/**
 * OVERLAP predicate (shared by the preview flag and the reducer guard) —
 * RULING-5 (2026-08-29): ANY active special for the pair blocks a batch
 * write, regardless of validity window — deactivate the standing row first
 * (its own audited save). ISO-window intersection was the pre-ruling
 * semantic. An INACTIVE row never blocks.
 */
export function hasBlockingActiveSpecial(
  dataset: PricingDataset,
  customerId: number,
  productId: string
): boolean {
  return dataset.specialItemPrices.some(
    (row) => row.isActive && row.customerId === customerId && row.productId === productId
  );
}

/**
 * RULING-6 predicate (shared by the preview flag and the reducer guard):
 * does the product have ANY cost record resolvable at `asOf`
 * (`effectiveDate <= asOf`, any source)? A markup_pct special without one is
 * dead at resolve time — and a time bomb after (springs to life unreviewed
 * when a cost record is later created).
 */
export function hasResolvableCostBase(
  dataset: PricingDataset,
  productId: string,
  asOf: string
): boolean {
  return selectCostRecord(dataset.costRecords, productId, asOf) !== null;
}

/**
 * The exact rows a batch writes — combo expansion × the ONE adjustment.
 * Takes the dataset since Option B (2026-08-31): a `markup_pct` row is
 * stamped with the EFFECTIVE anchor of ITS product, which is a per-combo
 * answer, not a batch-level one.
 */
export function buildSpecialPriceBatchRows(
  input: SpecialPriceBatchInput,
  batchId: string,
  reason: string,
  actorName: string,
  createdAt: string,
  dataset: Pick<PricingDataset, 'productAnchors' | 'pricingAnchorDefault'>
): SpecialItemPrice[] {
  return expandSpecialPriceBatchCombos(input.productIds, input.customerIds).map((combo, index) => {
    const base = {
      id: `${batchId}-${index + 1}`,
      customerId: combo.customerId,
      productId: combo.productId,
      reason,
      validFrom: input.validFrom,
      validTo: input.validTo,
      isActive: true,
      createdBy: actorName,
      createdAt,
    };
    return input.adjustment.kind === 'fixed'
      ? {
          ...base,
          kind: 'fixed' as const,
          fixedPrice: input.adjustment.fixedPrice,
          currency: input.adjustment.currency,
        }
      : {
          ...base,
          kind: 'markup_pct' as const,
          markupPct: input.adjustment.markupPct,
          // B0 (2026-08-31) — stamp the basis this % was authored against.
          // Option B landed the same day: the resolver now multiplies the
          // figure THIS FIELD names, so the stamp is the product's EFFECTIVE
          // anchor rather than the old `'pricing_base'` literal (whose own
          // comment named this slice as the one that would change it).
          // Existing rows keep their recorded basis and do not reprice.
          basisAtAuthoring: effectiveAnchorOf(dataset, combo.productId),
        };
  });
}

/**
 * Append a whole batch of special-price rows ATOMICALLY. Guards (throw BEFORE
 * anything is written): BATCH_EMPTY, BATCH_CAP_EXCEEDED, BATCH_OVERLAP —
 * ANY combo whose pair already carries ANY active special (window ignored,
 * RULING-5) rejects the WHOLE batch — and BATCH_UNRESOLVABLE (RULING-6) —
 * any markup_pct row whose product has no resolvable cost record at
 * max(row.validFrom, asOf) rejects the WHOLE batch (freeze rows need no
 * base and pass). Refusal, never a partial write.
 */
export function applySpecialPriceBatchSave(
  dataset: PricingDataset,
  rows: SpecialItemPrice[],
  asOf: string
): PricingDataset {
  if (rows.length === 0) throw new PricingGuardError('BATCH_EMPTY');
  if (rows.length > SPECIAL_PRICE_BATCH_CAP) throw new PricingGuardError('BATCH_CAP_EXCEEDED');
  for (const row of rows) {
    if (hasBlockingActiveSpecial(dataset, row.customerId, row.productId)) {
      throw new PricingGuardError('BATCH_OVERLAP');
    }
  }
  for (const row of rows) {
    if (
      row.kind === 'markup_pct' &&
      !hasResolvableCostBase(dataset, row.productId, row.validFrom > asOf ? row.validFrom : asOf)
    ) {
      throw new PricingGuardError('BATCH_UNRESOLVABLE');
    }
  }
  return { ...dataset, specialItemPrices: [...dataset.specialItemPrices, ...rows] };
}

/**
 * D27 fence — cost records are AUD like every seed. The create shape below
 * has NO currency member, so a non-AUD record is unrepresentable from this
 * path (the CostRecordPatch unrepresentability precedent); a currency picker
 * on the create form is likewise refused this wave.
 */
const COST_RECORD_CURRENCY = 'AUD';

/**
 * Cost-record CREATE inputs (S2.4) — the sealed-legal 來源 write path:
 * `source` (CH|TW) is chosen HERE, at create, and only here — after create it
 * is read-only on every surface (`CostRecordPatch` has no `source` member).
 * `currency` / `fxSnapshot` are deliberately ABSENT (AUD-only, D27; an FX
 * snapshot only ever comes from a purchase document, never a form).
 */
export interface CostRecordCreate {
  id: string;
  productId: string;
  source: CostRecord['source'];
  basis: CostRecord['basis'];
  /** Enterable at CREATE only — read-only everywhere after (plan §S9.1). */
  purchaseCost: number;
  landedAllocation: number;
  internalMarkupPct: number;
  salesMarkupPct: number;
  effectiveDate: string;
}

/**
 * Append a new cost record (S2.4). Guards (throw BEFORE anything is written):
 * duplicate id, product-exists against the caller-supplied product universe
 * (the store's dataset carries no product master, so the picker's option list
 * is the check), and non-negative money amounts. The new record then flows
 * into `selectCostRecord` resolution naturally — no resolver change.
 */
export function applyCostRecordAdd(
  dataset: PricingDataset,
  create: CostRecordCreate,
  knownProductIds: readonly string[]
): PricingDataset {
  if (dataset.costRecords.some((candidate) => candidate.id === create.id)) {
    throw new PricingGuardError('DUPLICATE_RECORD');
  }
  if (!knownProductIds.includes(create.productId)) {
    throw new PricingGuardError('PRODUCT_NOT_FOUND');
  }
  if (create.purchaseCost < 0 || create.landedAllocation < 0) {
    throw new PricingGuardError('NEGATIVE_COST_INPUT');
  }
  const record: CostRecord = { ...create, currency: COST_RECORD_CURRENCY, fxSnapshot: null };
  return { ...dataset, costRecords: [...dataset.costRecords, record] };
}

// ---------------------------------------------------------------------------
// S2.1 pricing-group edit / deactivate (the container the discount cards hang
// off — `applyPricingGroupAdd` itself lives in the domain package).
// ---------------------------------------------------------------------------

/**
 * Editable pricing-group fields (S2.1). `code` and `isActive` are deliberately
 * ABSENT: the code is immutable after creation (products and discount cards
 * reference it — the CostRecordPatch unrepresentability precedent), and
 * activation is its own audited action (`setPricingGroupActive`), never a
 * side-effect of an edit.
 */
export interface PricingGroupPatch {
  nameEn?: string;
  nameZh?: string;
  /** New policy rate; `null` clears it (the group reverts to "no rate"). */
  defaultDiscountPct?: number | null;
  /** New taxonomy link; `null` clears it. */
  categoryId?: string | null;
}

function mergePricingGroupPatch(group: PricingGroup, patch: PricingGroupPatch): PricingGroup {
  const next: PricingGroup = { ...group };
  if (patch.nameEn !== undefined) next.nameEn = patch.nameEn;
  if (patch.nameZh !== undefined) next.nameZh = patch.nameZh;
  if (patch.categoryId !== undefined) next.categoryId = patch.categoryId;
  if (patch.defaultDiscountPct !== undefined) {
    // The field is optional-not-nullable on PricingGroup, so "cleared" is the
    // absent key — never a stored null.
    if (patch.defaultDiscountPct === null) delete next.defaultDiscountPct;
    else next.defaultDiscountPct = patch.defaultDiscountPct;
  }
  return next;
}

/** Patch one pricing group in place (S2.1) — code/isActive untouched by construction. */
export function applyPricingGroupUpdate(
  dataset: PricingDataset,
  groupId: string,
  patch: PricingGroupPatch
): PricingDataset {
  return {
    ...dataset,
    pricingGroups: (dataset.pricingGroups ?? []).map((group) =>
      group.id === groupId ? mergePricingGroupPatch(group, patch) : group
    ),
  };
}

/**
 * Flip one group's isActive (S2.1). No hard delete exists for groups —
 * deactivated rows stay listed with the house inactive treatment.
 */
export function applyPricingGroupActiveSet(
  dataset: PricingDataset,
  groupId: string,
  isActive: boolean
): PricingDataset {
  return {
    ...dataset,
    pricingGroups: (dataset.pricingGroups ?? []).map((group) =>
      group.id === groupId ? { ...group, isActive } : group
    ),
  };
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

let auditSeq = 0;
const nextAuditId = () => `po-audit-${Date.now()}-${(auditSeq += 1)}`;

/** One PRICE_OVERRIDE audit row (S4.1 shared shape for the new saves). */
function buildAuditRow(
  entityType: PriceOverrideEntityType,
  entityId: string,
  summary: string,
  reason: string,
  actorName: string
): PriceOverrideAuditRow {
  return {
    id: nextAuditId(),
    action: 'PRICE_OVERRIDE',
    entityType,
    entityId,
    summary,
    reason: reason.trim(),
    actorName,
    createdAt: new Date().toISOString(),
  };
}

/**
 * B2.5b' round-trip: fire the write, then reconcile.
 *
 * `before` is the dataset as it stood BEFORE the optimistic apply. On success
 * the store adopts the server's audit row (replacing the optimistic one, whose
 * id and timestamp were client-invented). On failure the pre-save dataset is
 * restored and `writeError` is set — nothing stays on screen that the backend
 * refused.
 *
 * Deliberately fire-and-reconcile rather than await-in-the-action: the pinned
 * surfaces call these actions synchronously and rely on a SYNCHRONOUS throw
 * for the guard path (ReasonRequiredModal). Making the action async would turn
 * every guard rejection into an unhandled promise, so the guard stays sync and
 * only the transport is deferred.
 */
function roundTrip(
  before: PricingDataset,
  optimisticAuditId: string | null,
  send: () => Promise<{ audit: PriceOverrideAuditRow | null }>,
): void {
  void send().then(
    (result) => {
      const audit = result.audit;
      if (!audit) return;
      usePricingStore.setState((state) => ({
        auditRows:
          optimisticAuditId === null
            ? [audit, ...state.auditRows]
            : state.auditRows.map((row) => (row.id === optimisticAuditId ? audit : row)),
      }));
    },
    (error: unknown) => {
      usePricingStore.setState((state) => ({
        dataset: before,
        auditRows: state.auditRows.filter((row) => row.id !== optimisticAuditId),
        writeError:
          error instanceof PricingAdminError
            ? error
            : new PricingAdminError('VALIDATION', null),
      }));
    },
  );
}

export interface PricingState {
  /**
   * The dataset the surfaces render. For the write-through saves the
   * ENDPOINTS hold the truth (B2.5b') and this is the client's view of it;
   * for the store-first saves it is still the client-side authority, carried
   * across a reload by the demo `persist` slice below.
   */
  dataset: PricingDataset | null;
  /** PRICE_OVERRIDE review rows, newest first. */
  auditRows: PriceOverrideAuditRow[];
  /**
   * Set when a round-trip was REFUSED after the optimistic apply (the dataset
   * has been rolled back). Surfaces render it through the shared
   * `pricing:pricing.adminErrors.<CODE>` copy.
   */
  writeError: PricingAdminError | null;
  clearWriteError: () => void;

  /** First-load hydration only — local edits are never clobbered by refetch. */
  hydrate: (dataset: PricingDataset) => void;
  upsertMarkupRules: (edits: MarkupRuleEdit[]) => void;
  addPriceLevel: (level: PriceLevel) => void;
  /** Guard: throws PricingGuardError('REASON_REQUIRED') on a blank reason. */
  saveSpecialPrice: (row: SpecialItemPrice, reason: string, actorName: string) => void;
  /** Activate/deactivate — also reason-gated (every save via ReasonRequiredModal). */
  setSpecialPriceActive: (id: string, isActive: boolean, reason: string, actorName: string) => void;
  /**
   * S3.1 central batch special prices — S4.1 shape: reason gate → pure
   * reducer computed BEFORE set (BATCH_EMPTY / BATCH_CAP_EXCEEDED /
   * BATCH_OVERLAP / BATCH_UNRESOLVABLE throw and nothing is written) → ONE
   * atomic set of dataset + ONE 'special_price_batch' audit HEADER row
   * (counts + adjustment) + one per-row 'special_price' entry per combo. A
   * single reason covers the whole batch.
   */
  saveSpecialPriceBatch: (input: SpecialPriceBatchInput, reason: string, actorName: string) => void;
  /**
   * S1.2 audit completeness: cost-basis edits are price-affecting, so the
   * save follows the S4.1 shape — reason-gated (REASON_REQUIRED), audited as
   * entityType 'cost_record' with an old → new summary.
   */
  updateCostRecord: (
    recordId: string,
    patch: CostRecordPatch,
    reason: string,
    actorName: string
  ) => void;
  /**
   * S2.4 cost-record CREATE — the one place 來源 (CH|TW) is ever chosen.
   * S4.1 shape: reason gate → pure reducer (DUPLICATE_RECORD /
   * PRODUCT_NOT_FOUND / NEGATIVE_COST_INPUT throw before anything is
   * written) → atomic dataset + 'cost_record' PRICE_OVERRIDE audit row.
   * `knownProductIds` = the product universe the picker rendered (the
   * pricing dataset carries no product master to check against).
   */
  addCostRecord: (
    create: CostRecordCreate,
    knownProductIds: readonly string[],
    reason: string,
    actorName: string
  ) => void;
  /**
   * First-class audit append (S1.2) for writers whose dataset mutation lives
   * elsewhere — the SO-line manual-override path (soPriceOverrideAudit.ts)
   * lands its persisted rows here instead of a raw setState. Rows arrive
   * fully built; the store only prepends.
   */
  appendAuditRow: (row: PriceOverrideAuditRow) => void;
  /**
   * D17 — company-wide Pricing Anchor default (control on Price Setup;
   * product surfaces read-only).
   *
   * ⚠ RETRACTED 2026-08-31 — this docblock read "NOT price-affecting this
   * wave: the resolver does not read it, so no reason gate yet … The gate
   * arrives with the resolver-anchor engine wave." THAT WAVE LANDED (Option B).
   * Moving this default now moves every ladder price for every product that
   * follows it, and this file's own contract is that price-affecting saves are
   * reason-gated with a `PRICE_OVERRIDE` audit row.
   *
   * OPEN — OWNER: Melissa (the control is manager-gated on Price Setup ›
   * Cost Basis). Deliberately NOT closed inside the arithmetic slice: adding a
   * ReasonRequiredModal to that Segmented is an operator-facing behaviour
   * change on a surface nobody reviewed for it, and smuggling it in beside a
   * price flip is how a UI decision gets made by a developer. Named here
   * rather than left as a silent inconsistency.
   */
  setPricingAnchorDefault: (anchor: PricingAnchor) => void;
  /**
   * Company costing-method default — config-only, and STILL config-only after
   * the Option-B flip: a costing method is a valuation policy, not a
   * determination input. Price Setup owns the control; no reason gate.
   * (Pinned by "costingMethodDefault still moves NO number" in
   * `src/__tests__/pricingAnchorDefault.test.ts`.)
   */
  setCostingMethodDefault: (method: CostingMethodDefault) => void;
  /**
   * Company MINIMUM MARGIN (2026-09-15) — config-only: it never moves a
   * price, it decides whether an SO enters the `low_margin` price-review
   * hold. `null` clears the floor. No reason gate (not price-affecting);
   * manager-gated on Price Setup › Cost Basis like the two defaults above.
   * The VALUE is the decision owner's — absent until ruled; a manager types it.
   * Also the server-overlay seat: `usePricingDataset` re-applies the fetched
   * value on every success so the store can never outlive the handler's row.
   */
  setMinimumMarginPct: (value: number | null) => void;

  // S4.1 price-affecting saves — ALL reason-gated (REASON_REQUIRED → the
  // ReasonRequiredModal is the only entry) and every accepted save appends a
  // PRICE_OVERRIDE audit row. Guards (fixed-price immutability, schedule
  // either-or) throw BEFORE anything is written.
  saveQuantitySchedule: (
    schedule: QuantityPricingSchedule,
    reason: string,
    actorName: string
  ) => void;
  saveCustomerGroupDiscount: (
    cardRow: CustomerGroupDiscount,
    reason: string,
    actorName: string
  ) => void;
  saveCurrencyPriceRow: (priceRow: CurrencyPriceRow, reason: string, actorName: string) => void;
  saveProductPricing: (fields: ProductPricingFields, reason: string, actorName: string) => void;
  /** Group creation is not price-affecting until a card references it — no reason gate (mirrors addPriceLevel). */
  addPricingGroup: (group: PricingGroup) => void;
  /**
   * S2.1 group edit — names / policy rate / taxonomy link only (the patch type
   * makes a code or isActive change unrepresentable). Reason-gated per the
   * slice contract and audited as entityType 'pricing_group' even though the
   * fields are config-only: the group is what discount cards hang off, so its
   * edits belong in the same review lens.
   */
  updatePricingGroup: (
    groupId: string,
    patch: PricingGroupPatch,
    reason: string,
    actorName: string
  ) => void;
  /**
   * S2.1 deactivate/activate — no hard delete; a deactivated group stays
   * listed (house inactive treatment). Reason-gated + audited like
   * setSpecialPriceActive.
   */
  setPricingGroupActive: (
    groupId: string,
    isActive: boolean,
    reason: string,
    actorName: string
  ) => void;
  /**
   * P2 band CRUD — create. Band edits ARE price-affecting under RULING R-BAND
   * (a band is what CHOOSES the markup rule a level price is computed from),
   * so this is reason-gated exactly like the other price-affecting saves —
   * unlike `addPricingGroup`, whose creation moves no number until a discount
   * card references it. A new cut re-partitions an existing cover the moment
   * it lands, so there is no "inert until referenced" state to exempt.
   * May throw 'DUPLICATE_RECORD' or a band-set violation — nothing written.
   */
  addPriceGroupBand: (
    create: PriceGroupBandCreate,
    reason: string,
    actorName: string
  ) => void;
  /**
   * P2 band CRUD — edit the cuts (letter / lower bound / upper bound). The
   * patch type has no currency, productGroupId or isActive member, so a band
   * can never migrate between sets and activation is never a side effect of
   * an edit. May throw 'RECORD_NOT_FOUND' or a band-set violation.
   */
  updatePriceGroupBand: (
    bandId: string,
    patch: PriceGroupBandPatch,
    reason: string,
    actorName: string
  ) => void;
  /**
   * P2 band CRUD — deactivate/activate. No hard delete; a deactivated cut
   * stays listed (house inactive treatment) and stops resolving. Refused with
   * 'BAND_GAP' when it would hole the cover — widen a neighbour first.
   */
  setPriceGroupBandActive: (
    bandId: string,
    isActive: boolean,
    reason: string,
    actorName: string
  ) => void;
  /**
   * P3 band CRUD — move a SHARED CUT. Same patch shape as
   * `updatePriceGroupBand`, but the touching neighbour's matching bound moves
   * with it in ONE atomic write, because a shared boundary is one number
   * written on two rows. Use this ONLY after the surface has shown the
   * operator which neighbour moves and from what to what
   * (`planBandCutMoves`); the strict `updatePriceGroupBand` stays the path for
   * an edit that touches no neighbour. May throw 'RECORD_NOT_FOUND' or a
   * band-set violation — the invariant still adjudicates the RESULT, so a move
   * past a neighbour's far bound is still refused and nothing is written.
   */
  moveBandCut: (
    bandId: string,
    patch: PriceGroupBandPatch,
    reason: string,
    actorName: string
  ) => void;
  reset: () => void;
}

function summarizeSpecialPrice(row: SpecialItemPrice): string {
  return row.kind === 'fixed'
    ? `fixed ${row.fixedPrice.toFixed(2)} ${row.currency}`
    : `markup ${row.markupPct}%`;
}

/** Batch header one-liner: counts + the ONE adjustment (freeze vocabulary). */
function summarizeSpecialPriceBatch(input: SpecialPriceBatchInput, comboCount: number): string {
  const adjustment =
    input.adjustment.kind === 'fixed'
      ? `freeze ${input.adjustment.fixedPrice.toFixed(2)} ${input.adjustment.currency}`
      : `markup ${input.adjustment.markupPct}%`;
  return (
    `batch ${comboCount} combos ` +
    `(${input.productIds.length}×${input.customerIds.length}) · ${adjustment}`
  );
}

/** Editable cost-basis inputs one-liner (Purchase Cost never appears — read-only). */
function summarizeCostRecord(record: CostRecord): string {
  return (
    `alloc ${record.landedAllocation.toFixed(2)} · int ${record.internalMarkupPct}% · ` +
    `sales ${record.salesMarkupPct}% · ${record.basis} · eff ${record.effectiveDate}`
  );
}

function summarizeQuantitySchedule(schedule: QuantityPricingSchedule): string {
  const brackets = schedule.brackets
    .map((bracket) => `${bracket.minQuantity}+→−${bracket.discountPct}%`)
    .join(' / ');
  return `${schedule.code} [${brackets}]`;
}

function summarizeGroupDiscount(cardRow: CustomerGroupDiscount): string {
  return cardRow.kind === 'discount_pct'
    ? `group ${cardRow.groupId} −${cardRow.discountPct}%`
    : `group ${cardRow.groupId} → level ${cardRow.priceLevelId}`;
}

/**
 * Band one-liner for the audit trail: the cut as an operator reads it, the
 * currency it is expressed in (D27 — bands are per-currency objects), the set
 * it belongs to, and its activation. `∞` is the open-ended top band; the set
 * is written as its owning product group, or `default` for the unscoped set.
 */
function summarizePriceGroupBand(band: PriceGroupBand): string {
  const upper = band.maxExclusive === null ? '∞' : band.maxExclusive.toString();
  const scope = band.productGroupId ?? 'default';
  return (
    `${band.group} [${band.minInclusive}, ${upper}) ${band.currency} · set ${scope}` +
    `${band.isActive === false ? ' · inactive' : ''}`
  );
}

/** Editable group fields one-liner (code appears once in the row prefix, never as a change). */
function summarizePricingGroup(group: PricingGroup): string {
  const rate = group.defaultDiscountPct != null ? `disc ${group.defaultDiscountPct}%` : 'disc —';
  const category = group.categoryId ? ` · cat ${group.categoryId}` : '';
  return `${group.nameZh}/${group.nameEn} · ${rate}${category}`;
}

function summarizeCurrencyPriceRow(priceRow: CurrencyPriceRow): string {
  const cell = priceRow.priceLevelId === null ? 'list' : priceRow.priceLevelId;
  return `${priceRow.currency} ${cell} ${priceRow.price.toFixed(2)}${
    priceRow.fixedPrice ? ' (fixed)' : ''
  }`;
}

function summarizeProductPricing(fields: ProductPricingFields): string {
  const parts: string[] = [];
  if (fields.pricingGroupId != null) parts.push(`group ${fields.pricingGroupId}`);
  if (fields.quantityScheduleId != null) parts.push(`schedule ${fields.quantityScheduleId}`);
  if ((fields.quantityBrackets?.length ?? 0) > 0) {
    parts.push(`${fields.quantityBrackets?.length} ad-hoc brackets`);
  }
  if (fields.costEstimateType != null) parts.push(`estimate ${fields.costEstimateType}`);
  return parts.length > 0 ? parts.join(' · ') : 'cleared';
}

// ---------------------------------------------------------------------------
// S1.1 demo persistence — dev/mock-mode only ("edit → refresh → still there").
// Orthogonal to (and removable at) the future real-API write-handler lane.
// ---------------------------------------------------------------------------

/** What survives a reload: the demo dataset + its audit trail, nothing else. */
interface PersistedPricingSlice {
  dataset: PricingDataset | null;
  auditRows: PriceOverrideAuditRow[];
}

/** Version-mismatch rehydrate lands here: drop persisted, seeds re-hydrate. */
const EMPTY_PERSISTED: PersistedPricingSlice = { dataset: null, auditRows: [] };

/**
 * Same gate as `overridesAllowed()` in featureFlags.ts: the dev server and
 * mock (demo) builds persist demo pricing; a real-API build (no VITE_USE_MSW,
 * or `--mode production`) never writes demo data to localStorage — its
 * storage is a no-op, so the key is simply never created.
 */
const demoPersistenceAllowed = (): boolean =>
  Boolean(import.meta.env.DEV) ||
  (import.meta.env.VITE_USE_MSW === 'true' && import.meta.env.MODE !== 'production');

const noopStorage: StateStorage = {
  getItem: () => null,
  setItem: () => undefined,
  removeItem: () => undefined,
};

export const PRICING_PERSIST_KEY = 'pricing-storage';

export const usePricingStore = create<PricingState>()(
  devtools(
    persist(
      (set, get) => ({
        dataset: null,
        auditRows: [],
        writeError: null,

        clearWriteError: () => set({ writeError: null }, false, 'pricing/clearWriteError'),

        hydrate: (dataset) =>
          set((state) => (state.dataset === null ? { dataset } : {}), false, 'pricing/hydrate'),

        upsertMarkupRules: (edits) =>
          set(
            (state) => {
              if (!state.dataset) return {};
              let next = state.dataset;
              for (const edit of edits) {
                next = applyMarkupRuleEdit(next, edit);
              }
              return { dataset: next };
            },
            false,
            'pricing/upsertMarkupRules'
          ),

        addPriceLevel: (level) => {
          const before = get().dataset;
          if (!before) return;
          set({ dataset: applyPriceLevelAdd(before, level) }, false, 'pricing/addPriceLevel');
          roundTrip(before, null, () => pricingApi.saveLevel(level));
        },

        saveSpecialPrice: (row, reason, actorName) => {
          if (!reason.trim()) throw new PricingGuardError('REASON_REQUIRED');
          const before = get().dataset;
          let auditId: string | null = null;
          const previous = get().dataset?.specialItemPrices.find(
            (candidate) => candidate.id === row.id
          );
          const summary = previous
            ? `${summarizeSpecialPrice(previous)} → ${summarizeSpecialPrice(row)}`
            : summarizeSpecialPrice(row);
          set(
            (state) => {
              if (!state.dataset) return {};
              const audit: PriceOverrideAuditRow = {
                id: nextAuditId(),
                action: 'PRICE_OVERRIDE',
                entityType: 'special_price',
                entityId: row.id,
                summary,
                reason: reason.trim(),
                actorName,
                createdAt: new Date().toISOString(),
              };
              auditId = audit.id;
              return {
                dataset: applySpecialPriceSave(state.dataset, { ...row, reason: reason.trim() }),
                auditRows: [audit, ...state.auditRows],
              };
            },
            false,
            'pricing/saveSpecialPrice'
          );
          if (before) {
            roundTrip(before, auditId, () => pricingApi.saveSpecialPrice(row, reason, actorName));
          }
        },

        setSpecialPriceActive: (id, isActive, reason, actorName) => {
          if (!reason.trim()) throw new PricingGuardError('REASON_REQUIRED');
          const before = get().dataset;
          let auditId: string | null = null;
          const row = get().dataset?.specialItemPrices.find((candidate) => candidate.id === id);
          if (!row) throw new PricingGuardError('RECORD_NOT_FOUND');
          set(
            (state) => {
              if (!state.dataset) return {};
              const audit: PriceOverrideAuditRow = {
                id: nextAuditId(),
                action: 'PRICE_OVERRIDE',
                entityType: 'special_price',
                entityId: id,
                summary: `${summarizeSpecialPrice(row)} → ${isActive ? 'active' : 'inactive'}`,
                reason: reason.trim(),
                actorName,
                createdAt: new Date().toISOString(),
              };
              auditId = audit.id;
              return {
                dataset: applySpecialPriceSave(state.dataset, { ...row, isActive }),
                auditRows: [audit, ...state.auditRows],
              };
            },
            false,
            'pricing/setSpecialPriceActive'
          );
          if (before) {
            roundTrip(before, auditId, () =>
              pricingApi.setSpecialPriceActive(id, isActive, reason, actorName)
            );
          }
        },

        // -------------------------------------------------------------------
        // STORE-FIRST saves (no endpoint yet). Shape unchanged from S4.1:
        // reason gate → pure reducer (guards may throw — computed BEFORE set
        // so a rejected save writes NOTHING) → ONE atomic set of dataset +
        // PRICE_OVERRIDE audit row. The demo `persist` slice carries them
        // across a reload until the write surface grows their routes.
        // -------------------------------------------------------------------

        saveSpecialPriceBatch: (input, reason, actorName) => {
          if (!reason.trim()) throw new PricingGuardError('REASON_REQUIRED');
          const dataset = get().dataset;
          if (!dataset) return;
          const batchId = `spb-${Date.now()}`;
          const createdAt = new Date().toISOString();
          const rows = buildSpecialPriceBatchRows(
            input,
            batchId,
            reason.trim(),
            actorName,
            createdAt,
            dataset
          );
          // May throw BATCH_EMPTY / BATCH_CAP_EXCEEDED / BATCH_OVERLAP /
          // BATCH_UNRESOLVABLE — nothing written then (whole-batch refusal,
          // never partial). asOf = local date (pricingAsOf semantics) so the
          // RULING-6 cost-base check clamps to max(validFrom, today).
          const next = applySpecialPriceBatchSave(dataset, rows, dayjs().format('YYYY-MM-DD'));
          const header = buildAuditRow(
            'special_price_batch',
            batchId,
            summarizeSpecialPriceBatch(input, rows.length),
            reason,
            actorName
          );
          const rowAudits = rows.map((batchRow) =>
            buildAuditRow(
              'special_price',
              batchRow.id,
              `C${batchRow.customerId} × ${batchRow.productId}: ${summarizeSpecialPrice(batchRow)}`,
              reason,
              actorName
            )
          );
          set(
            (state) => ({
              dataset: next,
              auditRows: [header, ...rowAudits, ...state.auditRows],
            }),
            false,
            'pricing/saveSpecialPriceBatch'
          );
        },

        updateCostRecord: (recordId, patch, reason, actorName) => {
          if (!reason.trim()) throw new PricingGuardError('REASON_REQUIRED');
          const record = get().dataset?.costRecords.find((candidate) => candidate.id === recordId);
          if (!record) throw new PricingGuardError('RECORD_NOT_FOUND');
          // S1.2 — the cost-basis edit lands in the same PRICE_OVERRIDE lens as
          // every other price-affecting save (old → new one-liner).
          const summary =
            `${record.productId}/${record.source}: ` +
            `${summarizeCostRecord(record)} → ${summarizeCostRecord({ ...record, ...patch })}`;
          set(
            (state) =>
              state.dataset
                ? {
                    dataset: applyCostRecordPatch(state.dataset, recordId, patch),
                    auditRows: [
                      buildAuditRow('cost_record', recordId, summary, reason, actorName),
                      ...state.auditRows,
                    ],
                  }
                : {},
            false,
            'pricing/updateCostRecord'
          );
        },

        addCostRecord: (create, knownProductIds, reason, actorName) => {
          if (!reason.trim()) throw new PricingGuardError('REASON_REQUIRED');
          const dataset = get().dataset;
          if (!dataset) return;
          // May throw DUPLICATE_RECORD / PRODUCT_NOT_FOUND /
          // NEGATIVE_COST_INPUT — nothing written then.
          const next = applyCostRecordAdd(dataset, create, knownProductIds);
          const record = next.costRecords[next.costRecords.length - 1];
          const summary =
            `${record.productId}/${record.source}: created · ` +
            `purchase ${record.purchaseCost.toFixed(2)} ${record.currency} · ` +
            summarizeCostRecord(record);
          set(
            (state) => ({
              dataset: next,
              auditRows: [
                buildAuditRow('cost_record', create.id, summary, reason, actorName),
                ...state.auditRows,
              ],
            }),
            false,
            'pricing/addCostRecord'
          );
        },

        appendAuditRow: (row) =>
          set(
            (state) => ({ auditRows: [row, ...state.auditRows] }),
            false,
            'pricing/appendAuditRow'
          ),

        setPricingAnchorDefault: (anchor) =>
          set(
            (state) =>
              state.dataset ? { dataset: { ...state.dataset, pricingAnchorDefault: anchor } } : {},
            false,
            'pricing/setPricingAnchorDefault'
          ),

        setCostingMethodDefault: (method) =>
          set(
            (state) =>
              state.dataset ? { dataset: { ...state.dataset, costingMethodDefault: method } } : {},
            false,
            'pricing/setCostingMethodDefault'
          ),

        // Identity-stable (2026-09-16): an overlay carrying the value the
        // store already holds must NOT mint a new dataset object — every
        // `[dataset]` subscriber (and the Cost Basis draft) would re-seed.
        setMinimumMarginPct: (value) =>
          set(
            (state) =>
              state.dataset && (state.dataset.minimumMarginPct ?? null) !== value
                ? { dataset: { ...state.dataset, minimumMarginPct: value } }
                : {},
            false,
            'pricing/setMinimumMarginPct'
          ),

        // ---------------------------------------------------------------------
        // S4.1 saves, B2.5b' write-through. Shared shape: reason guard → shared
        // guard + pure reducer (may throw — computed BEFORE set so a rejected
        // save writes NOTHING) → atomic set of dataset + PRICE_OVERRIDE audit
        // row → round-trip, which adopts the server's row or rolls back.
        // ---------------------------------------------------------------------

        saveQuantitySchedule: (schedule, reason, actorName) => {
          if (!reason.trim()) throw new PricingGuardError('REASON_REQUIRED');
          const dataset = get().dataset;
          if (!dataset) return;
          const audit = buildAuditRow('quantity_schedule', schedule.id, '', reason, actorName);
          const previous = dataset.quantityPricingSchedules?.find(
            (candidate) => candidate.id === schedule.id
          );
          const next = applyQuantityScheduleSave(dataset, schedule);
          const summary = previous
            ? `${summarizeQuantitySchedule(previous)} → ${summarizeQuantitySchedule(schedule)}`
            : summarizeQuantitySchedule(schedule);
          set(
            (state) => ({
              dataset: next,
              auditRows: [{ ...audit, summary }, ...state.auditRows],
            }),
            false,
            'pricing/saveQuantitySchedule'
          );
          roundTrip(dataset, audit.id, () =>
            pricingApi.saveQuantitySchedule(schedule, reason, actorName)
          );
        },

        saveCustomerGroupDiscount: (cardRow, reason, actorName) => {
          if (!reason.trim()) throw new PricingGuardError('REASON_REQUIRED');
          const dataset = get().dataset;
          if (!dataset) return;
          const audit = buildAuditRow('customer_group_discount', cardRow.id, '', reason, actorName);
          const previous = dataset.customerGroupDiscounts?.find(
            (candidate) =>
              candidate.customerId === cardRow.customerId && candidate.groupId === cardRow.groupId
          );
          const next = applyCustomerGroupDiscountSave(dataset, cardRow);
          const summary = previous
            ? `C${cardRow.customerId}: ${summarizeGroupDiscount(previous)} → ${summarizeGroupDiscount(cardRow)}`
            : `C${cardRow.customerId}: ${summarizeGroupDiscount(cardRow)}`;
          set(
            (state) => ({
              dataset: next,
              auditRows: [{ ...audit, summary }, ...state.auditRows],
            }),
            false,
            'pricing/saveCustomerGroupDiscount'
          );
          roundTrip(dataset, audit.id, () =>
            pricingApi.saveGroupDiscount(cardRow, reason, actorName)
          );
        },

        saveCurrencyPriceRow: (priceRow, reason, actorName) => {
          if (!reason.trim()) throw new PricingGuardError('REASON_REQUIRED');
          const dataset = get().dataset;
          if (!dataset) return;
          const audit = buildAuditRow('currency_price', priceRow.id, '', reason, actorName);
          const previous = dataset.currencyPriceRows?.find(
            (candidate) =>
              candidate.id === priceRow.id ||
              (candidate.productId === priceRow.productId &&
                candidate.currency === priceRow.currency &&
                (candidate.priceLevelId ?? null) === (priceRow.priceLevelId ?? null))
          );
          // FIXED_PRICE_LOCKED (Discribe-21) — the guard runs BEFORE the reducer,
          // so a refused save writes nothing (B2.5a': guards and reducers are
          // separate exports; the store composes them, as the server does).
          const locked = guardCurrencyPriceRowSave(dataset, priceRow);
          if (locked) throw locked;
          const next = applyCurrencyPriceRowSave(dataset, priceRow);
          const summary = previous
            ? `${priceRow.productId}: ${summarizeCurrencyPriceRow(previous)} → ${summarizeCurrencyPriceRow(priceRow)}`
            : `${priceRow.productId}: ${summarizeCurrencyPriceRow(priceRow)}`;
          set(
            (state) => ({
              dataset: next,
              auditRows: [{ ...audit, summary }, ...state.auditRows],
            }),
            false,
            'pricing/saveCurrencyPriceRow'
          );
          roundTrip(dataset, audit.id, () =>
            pricingApi.saveCurrencyPriceRow(priceRow, reason, actorName)
          );
        },

        saveProductPricing: (fields, reason, actorName) => {
          if (!reason.trim()) throw new PricingGuardError('REASON_REQUIRED');
          const dataset = get().dataset;
          if (!dataset) return;
          const audit = buildAuditRow('product_pricing', fields.productId, '', reason, actorName);
          const previous = dataset.productPricing?.find(
            (candidate) => candidate.productId === fields.productId
          );
          // SCHEDULE_EITHER_OR (DD!116) — guard first, nothing written on refusal.
          const eitherOr = guardProductPricingSave(dataset, fields);
          if (eitherOr) throw eitherOr;
          const next = applyProductPricingSave(dataset, fields);
          const summary = previous
            ? `${summarizeProductPricing(previous)} → ${summarizeProductPricing(fields)}`
            : summarizeProductPricing(fields);
          set(
            (state) => ({
              dataset: next,
              auditRows: [{ ...audit, summary }, ...state.auditRows],
            }),
            false,
            'pricing/saveProductPricing'
          );
          roundTrip(dataset, audit.id, () =>
            pricingApi.saveProductPricing(fields, reason, actorName)
          );
        },

        addPricingGroup: (group) => {
          const before = get().dataset;
          if (!before) return;
          set({ dataset: applyPricingGroupAdd(before, group) }, false, 'pricing/addPricingGroup');
          roundTrip(before, null, () => pricingApi.savePricingGroup(group));
        },

        updatePricingGroup: (groupId, patch, reason, actorName) => {
          if (!reason.trim()) throw new PricingGuardError('REASON_REQUIRED');
          const group = get().dataset?.pricingGroups?.find(
            (candidate) => candidate.id === groupId
          );
          if (!group) throw new PricingGuardError('RECORD_NOT_FOUND');
          const summary =
            `${group.code}: ` +
            `${summarizePricingGroup(group)} → ${summarizePricingGroup(mergePricingGroupPatch(group, patch))}`;
          set(
            (state) =>
              state.dataset
                ? {
                    dataset: applyPricingGroupUpdate(state.dataset, groupId, patch),
                    auditRows: [
                      buildAuditRow('pricing_group', groupId, summary, reason, actorName),
                      ...state.auditRows,
                    ],
                  }
                : {},
            false,
            'pricing/updatePricingGroup'
          );
        },

        setPricingGroupActive: (groupId, isActive, reason, actorName) => {
          if (!reason.trim()) throw new PricingGuardError('REASON_REQUIRED');
          const group = get().dataset?.pricingGroups?.find(
            (candidate) => candidate.id === groupId
          );
          if (!group) throw new PricingGuardError('RECORD_NOT_FOUND');
          const summary =
            `${group.code}: ${group.isActive ? 'active' : 'inactive'} → ` +
            (isActive ? 'active' : 'inactive');
          set(
            (state) =>
              state.dataset
                ? {
                    dataset: applyPricingGroupActiveSet(state.dataset, groupId, isActive),
                    auditRows: [
                      buildAuditRow('pricing_group', groupId, summary, reason, actorName),
                      ...state.auditRows,
                    ],
                  }
                : {},
            false,
            'pricing/setPricingGroupActive'
          );
        },

        // -------------------------------------------------------------------
        // P2 band CRUD. Same shape as every price-affecting save: reason gate
        // → pure reducer (may throw; computed BEFORE `set`, so a refused save
        // writes NOTHING) → ONE atomic set of dataset + a
        // 'price_group_band' PRICE_OVERRIDE audit row.
        // -------------------------------------------------------------------

        addPriceGroupBand: (create, reason, actorName) => {
          if (!reason.trim()) throw new PricingGuardError('REASON_REQUIRED');
          const dataset = get().dataset;
          if (!dataset) return;
          // May throw DUPLICATE_RECORD / BAND_RANGE_INVALID /
          // BAND_OPEN_END_NOT_TOP / BAND_OVERLAP / BAND_GAP — nothing written.
          const next = applyPriceGroupBandAdd(dataset, create);
          const band = next.priceGroupBands[next.priceGroupBands.length - 1];
          set(
            (state) => ({
              dataset: next,
              auditRows: [
                buildAuditRow(
                  'price_group_band',
                  band.id,
                  `created · ${summarizePriceGroupBand(band)}`,
                  reason,
                  actorName
                ),
                ...state.auditRows,
              ],
            }),
            false,
            'pricing/addPriceGroupBand'
          );
        },

        updatePriceGroupBand: (bandId, patch, reason, actorName) => {
          if (!reason.trim()) throw new PricingGuardError('REASON_REQUIRED');
          const dataset = get().dataset;
          if (!dataset) return;
          const previous = dataset.priceGroupBands.find((candidate) => candidate.id === bandId);
          // May throw RECORD_NOT_FOUND or a band-set violation — nothing written.
          const next = applyPriceGroupBandUpdate(dataset, bandId, patch);
          const updated = next.priceGroupBands.find((candidate) => candidate.id === bandId);
          const summary =
            previous && updated
              ? `${summarizePriceGroupBand(previous)} → ${summarizePriceGroupBand(updated)}`
              : bandId;
          set(
            (state) => ({
              dataset: next,
              auditRows: [
                buildAuditRow('price_group_band', bandId, summary, reason, actorName),
                ...state.auditRows,
              ],
            }),
            false,
            'pricing/updatePriceGroupBand'
          );
        },

        setPriceGroupBandActive: (bandId, isActive, reason, actorName) => {
          if (!reason.trim()) throw new PricingGuardError('REASON_REQUIRED');
          const dataset = get().dataset;
          if (!dataset) return;
          const previous = dataset.priceGroupBands.find((candidate) => candidate.id === bandId);
          // May throw RECORD_NOT_FOUND, or BAND_GAP when deactivating an
          // INTERIOR cut would hole the cover — nothing written.
          const next = applyPriceGroupBandActiveSet(dataset, bandId, isActive);
          const summary = previous
            ? `${summarizePriceGroupBand(previous)} → ${isActive ? 'active' : 'inactive'}`
            : bandId;
          set(
            (state) => ({
              dataset: next,
              auditRows: [
                buildAuditRow('price_group_band', bandId, summary, reason, actorName),
                ...state.auditRows,
              ],
            }),
            false,
            'pricing/setPriceGroupBandActive'
          );
        },

        moveBandCut: (bandId, patch, reason, actorName) => {
          if (!reason.trim()) throw new PricingGuardError('REASON_REQUIRED');
          const dataset = get().dataset;
          if (!dataset) return;
          const previousById = new Map(
            dataset.priceGroupBands.map((band) => [band.id, band] as const)
          );
          // May throw RECORD_NOT_FOUND or a band-set violation — nothing written.
          const { dataset: next, moves } = applyPriceGroupBandCutMove(dataset, bandId, patch);
          const nextById = new Map(next.priceGroupBands.map((band) => [band.id, band] as const));
          // ONE audit row per band whose numbers moved — the edited cut FIRST,
          // then each neighbour that followed it. A single row standing in for
          // two rows is how a trail starts under-reporting a price change.
          const changedIds = [bandId, ...moves.map((move) => move.bandId)];
          const rows = changedIds.flatMap((id) => {
            const before = previousById.get(id);
            const after = nextById.get(id);
            if (!before || !after) return [];
            return [
              buildAuditRow(
                'price_group_band',
                id,
                `${summarizePriceGroupBand(before)} → ${summarizePriceGroupBand(after)}`,
                reason,
                actorName
              ),
            ];
          });
          set(
            (state) => ({ dataset: next, auditRows: [...rows, ...state.auditRows] }),
            false,
            'pricing/moveBandCut'
          );
        },

        reset: () =>
          set({ dataset: null, auditRows: [], writeError: null }, false, 'pricing/reset'),
      }),
      {
        name: PRICING_PERSIST_KEY,
        // The seed generation stamp — a persisted payload written against an
        // older SEED_VERSION mismatches at synchronous rehydrate and is
        // dropped via `migrate` below (reset-to-seeds path). Bump discipline:
        // src/stores/pricingSeedVersion.ts.
        version: SEED_VERSION,
        // Called ONLY on a version mismatch: stale demo data is dropped, never
        // migrated field-by-field — the next hydrate() refills from seeds.
        migrate: () => EMPTY_PERSISTED,
        // `writeError` is a live transport failure, not durable demo state —
        // it never leaves this session.
        partialize: (state): PersistedPricingSlice => ({
          dataset: state.dataset,
          auditRows: state.auditRows,
        }),
        storage: createJSONStorage(() =>
          demoPersistenceAllowed() && typeof localStorage !== 'undefined'
            ? localStorage
            : noopStorage
        ),
      }
    ),
    { name: 'PricingStore' }
  )
);
